"use client";

import React from "react";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <NavigationMenu className="max-w-full justify-start" viewport={false}>
      <NavigationMenuList className="flex-col items-start space-y-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="w-full">
            <NavigationMenuLink asChild className="h-auto w-full justify-start px-0 py-1 font-sans font-normal text-muted-foreground hover:bg-transparent hover:text-foreground hover:underline focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground">
              <Link to={item.href || "#"}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function NewsletterForm() {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };
  return <form className="flex flex-col space-y-3" onSubmit={handleSubmit}>
      <Input type="email" placeholder="Email address" className="bg-background" required aria-label="Email address for newsletter" />
      <Button type="submit" className="w-full group">
        Subscribe
        <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
      </Button>
    </form>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const socialLinks = [{
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "#",
    label: "Twitter"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }, {
    icon: Linkedin,
    href: "#",
    label: "LinkedIn"
  }];
  const contactInfo = [{
    icon: MapPin,
    text: "123 Community Lane, Cityville, State, ZIP"
  }, {
    icon: Phone,
    text: "(000) 000-0000"
  }, {
    icon: Mail,
    text: "info@mytrustfoundation.org"
  }];
  return <section id="footer" className="bg-background pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <Separator className="mb-16" />
        
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          {}
          <div className="flex flex-col items-start space-y-6">
            <Link to="/" className="inline-block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/trust_foundation_logo_dark.png" alt="The Trust Foundation Logo" className="h-10 w-auto object-contain" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/trust_foundation_logo_dark.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/trust_foundation_logo_dark.png-wvc-sizes" loading="lazy" />
            </Link>
            <p className="font-sans text-sm leading-relaxed text-muted-foreground">
              The Trust Foundation is a legally established 501(c)(3) nonprofit organization dedicated to restoring stability, dignity, and opportunity for individuals in need through supportive services and community partnership.
            </p>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="font-default text-lg font-semibold text-foreground">Quick Links</h3>
            <MenuProvider menu_id="44">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="font-default text-lg font-semibold text-foreground">Contact Us</h3>
            <ul className="flex flex-col space-y-4">
              {contactInfo.map((item, i) => {
              const Icon = item.icon;
              return <li key={i} data-index={i} className="flex items-start space-x-3">
                    <Icon className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
                    <span className="font-sans text-sm text-muted-foreground">{item.text}</span>
                  </li>;
            })}
            </ul>
            <div className="flex items-center space-x-4 pt-2">
              {socialLinks.map((social, i) => {
              const Icon = social.icon;
              return <Link key={i} data-index={i} to={social.href} className="text-muted-foreground transition-colors hover:text-primary" aria-label={social.label}>
                    <Icon className="size-5" />
                  </Link>;
            })}
            </div>
          </div>

          {}
          <div className="flex flex-col space-y-6">
            <h3 className="font-default text-lg font-semibold text-foreground">Stay Updated</h3>
            <p className="font-sans text-sm text-muted-foreground">
              Join our newsletter to receive updates on our programs, future initiatives, and ways to support our mission.
            </p>
            <FormProvider>
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <Separator className="my-12" />

        <div className="flex flex-col items-center justify-between space-y-4 text-center md:flex-row md:space-y-0 md:text-left">
          <p className="font-sans text-sm text-muted-foreground">
            &copy; {currentYear} The Trust Foundation. All rights reserved.
          </p>
          <div className="flex space-x-6 font-sans text-sm text-muted-foreground">
            <Link to="#" className="hover:text-foreground hover:underline">Privacy Policy</Link>
            <Link to="#" className="hover:text-foreground hover:underline">Terms of Service</Link>
          </div>
        </div>
      </div>
    </section>;
}